import { lazy, Suspense } from 'react';
import { getRadioConversationConfig } from './radioConversationConfig';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import { AuthContext } from '../../context/AuthContext';
import getShareUrl from '../../utils/getShareUrl';
import {
  EMPTY_RADIO_QUEUE,
  getActiveRadioQueue,
  mergeRadioCatalogItems,
  normalizeRadioCatalogResponseItems,
  RADIO_QUEUE_KINDS,
} from './hitLabRadioTracks';
import {
  buildRadioRecordCapsule,
  mergeCanonicalRadioMetrics,
  mergeLegacyRadioPlayCount,
} from './radioRecordCapsule';
import {
  getRadioProofSummaryFingerprint,
  normalizeRadioLiveCapsuleSignals,
} from './radioLiveCapsuleMessages';
import RadioRecordActions from './RadioRecordActions';
import RadioRecordCapsuleCard from './RadioRecordCapsuleCard';
import RadioLiveCapsule from './RadioLiveCapsule';
import useRadioParticipationOwner from './useRadioParticipationOwner';
import useRadioPlaybackAttempt from './useRadioPlaybackAttempt';
import RadioInvitationStatus from './RadioInvitationStatus';
import { captureRadioInteraction, consumeRadioInteraction, rememberRadioSession, presentRadioInvitation } from './radioParticipationIntent';
import { observeRadioCue, radioCueSuppressed } from './radioParticipationCue';
import useJgRadioLiveSignals from './useJgRadioLiveSignals';
import useRadioAudioLevels, {
  canAnalyzeRadioAudioQueue,
  getRadioAudioCrossOrigin,
} from './useRadioAudioLevels';
import HitLabRadioFeedbackSheet from './HitLabRadioFeedbackSheet';
import HitLabRadioJgkSupportSheet from './HitLabRadioJgkSupportSheet';
import { getJGRadioDockPlacement } from './jgRadioDockPlacement';
import { shouldShowJGRadioDock } from './jgRadioDockVisibility';
import {
  clearJGRadioCue,
  JG_RADIO_CUE_EVENT,
  readJGRadioCue,
} from './hitLabRadioCue';
import {
  buildRadioListenSessionId,
  getAudioListenSnapshot,
  RADIO_LISTEN_HEARTBEAT_MS,
  sendRadioListenEvent,
} from './radioListenEvents';
import './jgRadioDock.css';
import { getRadioStartupQueueIndex } from './radioStartup';
export { getRandomRadioTrackQueueIndex as getRandomTrackQueueIndex } from './radioStartup';

const RadioConversation = lazy(() => import('./RadioConversation'));
const MIN_PLAY_SECONDS = 30;
const MIN_PLAY_PERCENT = 25;
const MINIMIZED_STORAGE_KEY = 'jg:radioDock:minimized:v1';
const JG_LOGO_MARK_URL =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png';
export const NEW_ROTATION_SIGNAL_INITIAL_DELAY_MS = 1200;
export const NEW_ROTATION_SIGNAL_DISPLAY_MS = 7000;
export const NEW_ROTATION_SIGNAL_COOLDOWN_MS = 75000;
export const PROOF_SIGNAL_DISPLAY_MS = 7000;

const clampIndex = (index, length) => {
  if (!length) return 0;
  const next = Number(index);
  if (!Number.isFinite(next)) return 0;
  return ((Math.trunc(next) % length) + length) % length;
};

const findCueTrackQueueIndex = (queue = [], trackId) =>
  (Array.isArray(queue) ? queue : []).findIndex(
    (item) =>
      item?.kind === RADIO_QUEUE_KINDS.TRACK &&
      String(item.id || item.trackId || '') === String(trackId || ''),
  );

const getLocalUtcDayKey = () => new Date().toISOString().slice(0, 10);

const buildPlaySessionId = (trackId) =>
  [
    'jg-radio-dock',
    String(trackId || 'track').replace(/[^a-z0-9_-]/gi, '').slice(0, 48),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join(':');

const persistMinimizedState = (isMinimized) => {
  try {
    window.sessionStorage.setItem(
      MINIMIZED_STORAGE_KEY,
      isMinimized ? '1' : '0',
    );
  } catch (err) {
    // Session persistence is optional; the dock state remains usable.
  }
};

export default function JGRadioDock({
  isAuthenticated: isAuthenticatedOverride,
  queue = EMPTY_RADIO_QUEUE,
}) {
  const audioRef = useRef(null);
  const conversationRef = useRef(null);
  const radioOwnerRef = useRef(null);
  const conversationConfig = getRadioConversationConfig();
  const [conversationFocused, setConversationFocused] = useState(false);

  const feedbackSignalTimerRef = useRef(null);
  const newRotationSignalTimerRef = useRef(null);
  const newRotationSignalIndexRef = useRef(0);
  const newRotationSignalSuppressedUntilRef = useRef(0);
  const proofSignalTimerRef = useRef(null);
  const lastProofSignalFingerprintRef = useRef('');
  const playPostKeysRef = useRef(new Set());
  const metricsOwnerRef = useRef(null);
  const metricsRequestRef = useRef(0);
  const acceptedMetricsRef = useRef({});
  const listenSessionRef = useRef(null);
  const listenHeartbeatTimerRef = useRef(null);
  const durationRef = useRef(0);
  const pendingPlayAfterTrackChangeRef = useRef(false);
  const playSessionIdRef = useRef('');
  const hasInitializedDbQueueRef = useRef(false);
  const hasUserSelectedTrackRef = useRef(false);
  const history = useHistory();
  const location = useLocation();
  const { player } = useContext(AuthContext);
  const isAuthenticated =
    typeof isAuthenticatedOverride === 'boolean'
      ? isAuthenticatedOverride
      : Boolean(player);
  const shouldRender = shouldShowJGRadioDock({
    pathname: location.pathname,
    isAuthenticated,
  });
  const placement = getJGRadioDockPlacement(location.pathname);
  const currentRoute = `${location.pathname || '/'}${location.search || ''}`;
  useEffect(() => { setConversationFocused(false); }, [currentRoute, shouldRender]);

  const [radioQueue, setRadioQueue] = useState(queue);
  const [catalogState, setCatalogState] = useState('loading');
  const [cueGlow, setCueGlow] = useState(false);
  const participationState = useRef({});
  const [metricsByTrackId, setMetricsByTrackId] = useState(null);
  const [liveCapsuleSignals, setLiveCapsuleSignals] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(() =>
    getRadioStartupQueueIndex(getActiveRadioQueue(queue)),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [isMinimized, setIsMinimized] = useState(true);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [supportMode, setSupportMode] = useState(null);
  const [transientFeedbackSignal, setTransientFeedbackSignal] = useState(null);
  const [transientNewInRotationSignal, setTransientNewInRotationSignal] =
    useState(null);
  const [transientProofSummary, setTransientProofSummary] = useState(null);
  const [newRotationSignalVersion, setNewRotationSignalVersion] = useState(0);
  participationState.current = { playing: isPlaying, participationOpen: feedbackOpen || Boolean(supportMode), pathname: location.pathname };

  useEffect(() => {
    if (!shouldRender) return undefined;
    metricsOwnerRef.current = {};
    return () => {
      metricsOwnerRef.current = null;
      metricsRequestRef.current += 1;
    };
  }, [player?.token, shouldRender]);

  const applyLegacyPlayCount = useCallback((play) => {
    if (metricsOwnerRef.current) {
      setMetricsByTrackId((current) => mergeLegacyRadioPlayCount(current, play));
    }
  }, []);

  const refreshRadioMetrics = useCallback(async ({ shouldApply = () => true } = {}) => {
    const owner = metricsOwnerRef.current;
    if (!owner) return;
    const request = ++metricsRequestRef.current;
    try {
      const response = await axiosConn.get('hitlab/radio/metrics');
      if (metricsOwnerRef.current !== owner || !shouldApply()) return;
      const updates = mergeCanonicalRadioMetrics(null, response);
      if (!updates) return;
      Object.keys(updates).forEach((trackId) => {
        if (request < (acceptedMetricsRef.current[trackId] || 0)) {
          delete updates[trackId];
        } else {
          acceptedMetricsRef.current[trackId] = request;
        }
      });
      if (!Object.keys(updates).length) return;
      setMetricsByTrackId((current) => ({ ...current, ...updates }));
    } catch (err) {
      // Keep the last valid count; supplemental metrics must not affect playback.
    }
  }, []);

  const activeQueue = useMemo(
    () => getActiveRadioQueue(radioQueue),
    [radioQueue],
  );
  const currentItem =
    activeQueue[clampIndex(currentIndex, activeQueue.length)] || null;
  const baseCapsule = useMemo(
    () => buildRadioRecordCapsule(currentItem, metricsByTrackId),
    [currentItem, metricsByTrackId],
  );

  const loadRadioData = useCallback(
    async ({ shouldApply = () => true } = {}) => {
      setCatalogState('loading');
      const catalogRequest = player?.token
        ? axiosConn.get('hitlab/radio/catalog', {
            timeout: 10000,
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          })
        : axiosConn.get('hitlab/radio/catalog', { timeout: 10000 });
      const [catalogResult, , signalsResult] = await Promise.allSettled([
        catalogRequest,
        Promise.resolve(),
        axiosConn.get('hitlab/radio/live-capsule/signals', { timeout: 10000 }),
      ]);

      if (!shouldApply()) return;
      setCatalogState(catalogResult.status === 'fulfilled' ? 'ready' : 'error');
      void refreshRadioMetrics({ shouldApply });

      if (catalogResult.status === 'fulfilled') {
        const items = normalizeRadioCatalogResponseItems(catalogResult.value);
        const nextQueue = mergeRadioCatalogItems(items);
        setRadioQueue(nextQueue);
        if (
          nextQueue.length &&
          !hasInitializedDbQueueRef.current &&
          !hasUserSelectedTrackRef.current
        ) {
          hasInitializedDbQueueRef.current = true;
          setCurrentIndex(getRadioStartupQueueIndex(nextQueue, catalogResult.value?.data?.data?.startTrackId));
        }
      } else {
        setRadioQueue((current) =>
          current && current.length ? current : queue,
        );
      }

      if (signalsResult.status === 'fulfilled') {
        setLiveCapsuleSignals(
          normalizeRadioLiveCapsuleSignals(signalsResult.value),
        );
      } else {
        setLiveCapsuleSignals(null);
      }
    },
    [player?.token, queue, refreshRadioMetrics],
  );

  const radioLiveSignals = useJgRadioLiveSignals({
    capsule: baseCapsule,
    isPlaying,
    enabled: shouldRender,
    onRefreshCatalog: loadRadioData,
    onPlayCount: applyLegacyPlayCount,
  });

  const capsule = baseCapsule;
  const { begin: beginPlayback, invalidate: invalidatePlayback, schedule: schedulePlayback,
    explicitReason, onPause: handleAudioPause } = useRadioPlaybackAttempt({ audioRef,
      trackId: capsule?.trackId, audioUrl: capsule?.audioUrl, playerToken: player?.token, enabled: shouldRender });
  const canUseAudioReactiveVisuals = useMemo(
    () => canAnalyzeRadioAudioQueue(activeQueue),
    [activeQueue],
  );
  const radioAudioLevels = useRadioAudioLevels(audioRef, {
    audioUrl: capsule?.audioUrl,
    isPlaying,
    barCount: 7,
    enabled: shouldRender && canUseAudioReactiveVisuals,
  });

  const feedbackTrack = useMemo(() => {
    if (!capsule || capsule.kind !== RADIO_QUEUE_KINDS.TRACK) return null;
    return {
      id: capsule.trackId,
      kind: capsule.kind,
      songTitle: capsule.title,
      artistName: capsule.artistName,
      coverArtUrl: capsule.coverArtUrl,
      feedbackEligible: capsule.canGiveFeedback,
    };
  }, [capsule]);

  const clearNewRotationSignalTimer = useCallback(() => {
    if (newRotationSignalTimerRef.current) {
      window.clearTimeout(newRotationSignalTimerRef.current);
      newRotationSignalTimerRef.current = null;
    }
  }, []);

  const clearProofSignalTimer = useCallback(() => {
    if (proofSignalTimerRef.current) {
      window.clearTimeout(proofSignalTimerRef.current);
      proofSignalTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearProofSignalTimer();
    },
    [clearProofSignalTimer],
  );

  const cooldownNewRotationSignal = useCallback(() => {
    clearNewRotationSignalTimer();
    setTransientNewInRotationSignal(null);
    newRotationSignalSuppressedUntilRef.current =
      Date.now() + NEW_ROTATION_SIGNAL_COOLDOWN_MS;
    setNewRotationSignalVersion((current) => current + 1);
  }, [clearNewRotationSignalTimer]);

  useEffect(() => {
    if (!shouldRender) return undefined;
    let isMounted = true;

    void loadRadioData({
      shouldApply: () => isMounted,
    });
    return () => {
      isMounted = false;
    };
  }, [loadRadioData, shouldRender]);

  useEffect(() => {
    if (!shouldRender || !capsule) {
      clearProofSignalTimer();
      setTransientProofSummary(null);
      return undefined;
    }

    const proofSummary = liveCapsuleSignals?.proofSummary || null;
    const fingerprint = getRadioProofSummaryFingerprint(proofSummary);
    if (!fingerprint) {
      clearProofSignalTimer();
      setTransientProofSummary(null);
      lastProofSignalFingerprintRef.current = '';
      return undefined;
    }

    if (fingerprint === lastProofSignalFingerprintRef.current) {
      return undefined;
    }

    lastProofSignalFingerprintRef.current = fingerprint;
    clearProofSignalTimer();
    setTransientProofSummary(proofSummary);
    proofSignalTimerRef.current = window.setTimeout(() => {
      setTransientProofSummary((current) =>
        getRadioProofSummaryFingerprint(current) === fingerprint
          ? null
          : current,
      );
      proofSignalTimerRef.current = null;
    }, PROOF_SIGNAL_DISPLAY_MS);

    return undefined;
  }, [
    capsule,
    clearProofSignalTimer,
    liveCapsuleSignals?.proofSummary,
    shouldRender,
  ]);

  useEffect(() => {
    clearNewRotationSignalTimer();
    setTransientNewInRotationSignal(null);
    setCueGlow(false);
    const account = player?.data?.player?._id || player?.player?._id || 'anonymous';
    const suppressed = !shouldRender || !isMinimized || !capsule || transientFeedbackSignal ||
      radioCueSuppressed(participationState.current);
    if (suppressed) (liveCapsuleSignals?.newInRotation || []).forEach(signal =>
      observeRadioCue(signal, account, { suppressed: true }));

    if (!shouldRender || !isMinimized || !capsule || transientFeedbackSignal) {
      return undefined;
    }

    const eligibleSignals = (
      Array.isArray(liveCapsuleSignals?.newInRotation)
        ? liveCapsuleSignals.newInRotation
        : []
    ).filter(
      (signal) =>
        signal?.isFreshRotationSignalEligible === true &&
        signal?.trackId &&
        signal?.title &&
        signal?.artistName &&
        signal?.newInRotationUntil,
    );

    if (!eligibleSignals.length) return undefined;

    let isCancelled = false;

    const scheduleSignal = (delayMs) => {
      newRotationSignalTimerRef.current = window.setTimeout(() => {
        if (isCancelled) return;

        const signal =
          eligibleSignals[
            newRotationSignalIndexRef.current % eligibleSignals.length
          ];
        newRotationSignalIndexRef.current += 1;
        const suppressedNow = radioCueSuppressed(participationState.current) ||
          Boolean(liveCapsuleSignals?.programmingMessage || transientFeedbackSignal);
        const glow = observeRadioCue(signal, account, { suppressed: suppressedNow,
          enabled: process.env.REACT_APP_JG_RADIO_PARTICIPATION_CUE_ENABLED !== 'false' });
        setCueGlow(glow);
        setTransientNewInRotationSignal(signal);

        newRotationSignalTimerRef.current = window.setTimeout(() => {
          if (isCancelled) return;
          setTransientNewInRotationSignal(null);
          setCueGlow(false);
          scheduleSignal(NEW_ROTATION_SIGNAL_COOLDOWN_MS);
        }, NEW_ROTATION_SIGNAL_DISPLAY_MS);
      }, Math.max(0, delayMs));
    };

    const suppressedForMs =
      newRotationSignalSuppressedUntilRef.current - Date.now();
    scheduleSignal(
      Math.max(NEW_ROTATION_SIGNAL_INITIAL_DELAY_MS, suppressedForMs),
    );

    return () => {
      isCancelled = true;
      clearNewRotationSignalTimer();
    };
  }, [
    capsule?.trackId,
    clearNewRotationSignalTimer,
    isMinimized,
    liveCapsuleSignals,
    newRotationSignalVersion,
    shouldRender,
    transientFeedbackSignal,
    isPlaying,
    feedbackOpen,
    supportMode,
    player?.token,
  ]);

  useEffect(() => {
    setIsMinimized(true);
    persistMinimizedState(true);
    setFeedbackOpen(false);
    setSupportMode(null);
  }, [location.pathname]);

  useEffect(() => {
    const discardSuppressed = () => {
      if (!radioCueSuppressed(participationState.current)) return;
      const account = player?.data?.player?._id || player?.player?._id || 'anonymous';
      (liveCapsuleSignals?.newInRotation || []).forEach(signal => observeRadioCue(signal, account, { suppressed: true }));
      setCueGlow(false);
    };
    document.addEventListener('focusin', discardSuppressed);
    document.addEventListener('visibilitychange', discardSuppressed);
    return () => {
      document.removeEventListener('focusin', discardSuppressed);
      document.removeEventListener('visibilitychange', discardSuppressed);
    };
  }, [liveCapsuleSignals, player?.token]);

  useEffect(() => {
    setCurrentIndex((current) => clampIndex(current, activeQueue.length));
  }, [activeQueue.length]);

  useEffect(() => {
    if (capsule) return;
    setFeedbackOpen(false);
    setSupportMode(null);
  }, [capsule]);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  const clearListenHeartbeatTimer = useCallback(() => {
    if (listenHeartbeatTimerRef.current) {
      window.clearInterval(listenHeartbeatTimerRef.current);
      listenHeartbeatTimerRef.current = null;
    }
  }, []);

  const postListenEvent = useCallback(
    (eventType, sessionOverride = null) => {
      const session = sessionOverride || listenSessionRef.current;
      if (!session?.sessionId || !session?.trackId) return;
      sendRadioListenEvent({
        eventType,
        sessionId: session.sessionId,
        trackId: session.trackId,
        source: session.source,
        invitationReference: session.invitationReference,
        playerToken: player?.token,
        ...getAudioListenSnapshot(audioRef.current, durationRef.current),
      });
    },
    [player?.token],
  );

  const endListenSession = useCallback(() => {
    const session = listenSessionRef.current;
    if (!session) return;
    clearListenHeartbeatTimer();
    postListenEvent('end', session);
    listenSessionRef.current = null;
  }, [clearListenHeartbeatTimer, postListenEvent]);

  const startListenSession = useCallback((startReason) => {
    const trackId = String(capsule?.trackId || '').trim();
    if (!trackId) return;

    if (listenSessionRef.current?.trackId === trackId) return;
    if (listenSessionRef.current) endListenSession();

    const session = {
      sessionId: buildRadioListenSessionId('jg-radio-dock', trackId),
      trackId,
      source: 'jg_radio_dock',
      invitationReference: consumeRadioInteraction(player?.token, trackId, startReason),
    };
    rememberRadioSession(player?.token, trackId, session.sessionId);
    listenSessionRef.current = session;
    postListenEvent('start', session);
    clearListenHeartbeatTimer();
    listenHeartbeatTimerRef.current = window.setInterval(() => {
      postListenEvent('heartbeat');
    }, RADIO_LISTEN_HEARTBEAT_MS);
  }, [capsule?.trackId, clearListenHeartbeatTimer, endListenSession, postListenEvent]);

  useEffect(() => {
    const handlePageHide = () => {
      endListenSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') endListenSession();
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [endListenSession]);

  const pauseAudio = useCallback(({ reset = false } = {}) => {
    invalidatePlayback();
    endListenSession();
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
      if (reset) audio.currentTime = 0;
      audio.load?.();
    } catch (err) {
      // Browser media state can reject seeks; keep the dock usable.
    }
  }, [endListenSession, invalidatePlayback]);

  const applyRadioCue = useCallback(
    (cue) => {
      if (!shouldRender || !cue?.trackId || !activeQueue.length) return false;
      const nextIndex = findCueTrackQueueIndex(activeQueue, cue.trackId);
      if (nextIndex < 0) return false;
      hasUserSelectedTrackRef.current = true;
      cooldownNewRotationSignal();
      pendingPlayAfterTrackChangeRef.current = false;
      pauseAudio({ reset: true });
      setIsPlaying(false);
      setIsLoading(false);
      setCurrentIndex(nextIndex);
      setIsMinimized(false);
      persistMinimizedState(false);
      setFeedbackOpen(false);
      clearJGRadioCue();
      return true;
    },
    [activeQueue, cooldownNewRotationSignal, pauseAudio, shouldRender],
  );

  useEffect(() => {
    if (shouldRender) return;
    pauseAudio({ reset: true });
    setIsPlaying(false);
    setIsLoading(false);
    setErrorMessage('');
    setFeedbackOpen(false);
  }, [pauseAudio, shouldRender]);

  useEffect(() => {
    if (!shouldRender || !activeQueue.length) return undefined;

    const storedCue = readJGRadioCue();
    let cueEffectActive = true;
    if (storedCue?.trackId) {
      Promise.resolve().then(() => {
        if (cueEffectActive && presentRadioInvitation(`/hitlab/radio?track=${encodeURIComponent(storedCue.trackId)}`)) clearJGRadioCue();
      });
    }

    const handleCue = (event) => {
      const cue = event?.detail || readJGRadioCue();
      if (cue?.trackId) {
        presentRadioInvitation(`/hitlab/radio?track=${encodeURIComponent(cue.trackId)}`);
        clearJGRadioCue();
      }
    };

    window.addEventListener(JG_RADIO_CUE_EVENT, handleCue);
    return () => {
      cueEffectActive = false;
      window.removeEventListener(JG_RADIO_CUE_EVENT, handleCue);
    };
  }, [activeQueue.length, applyRadioCue, shouldRender]);

  const pauseForConversation = useCallback(() => {
    invalidatePlayback();
    endListenSession();
    pendingPlayAfterTrackChangeRef.current = false;
    audioRef.current?.pause();
    setIsPlaying(false);
    setIsLoading(false);
  }, [endListenSession, invalidatePlayback]);

  const playCurrent = useCallback(async (startReason = 'continuation') => {
    if (conversationRef.current?.prepareForRadioPlay() === false) return false;
    const attempt = beginPlayback();
    if (!attempt) return false;
    const acquired = await attempt.claim();
    if (!attempt.valid()) return false;
    if (!acquired) {
      setErrorMessage('JG Radio is playing in another tab. Pause it there, then press Play here.'); return false;
    }
    setErrorMessage('');
    setIsLoading(true);
    try {
      await attempt.play();
      if (!attempt.valid()) return false;
      if (!attempt.started()) { attempt.cancel(); setIsLoading(false); return false; }
      setIsPlaying(true);
      setIsLoading(false);
      startListenSession(startReason);
      return true;
    } catch (err) {
      if (!attempt.valid()) return false;
      attempt.cancel();
      setIsPlaying(false);
      setIsLoading(false);
      setErrorMessage('This audio item could not start. Try the next item.');
      return false;
    }
  }, [beginPlayback, startListenSession]);

  useEffect(() => {
    pauseAudio({ reset: true });
    playSessionIdRef.current = buildPlaySessionId(capsule?.trackId);
    setElapsed(0);
    setDuration(0);
    setErrorMessage('');
    setShareMessage('');
    setSupportMode(null);
    setIsLoading(false);
    setIsPlaying(false);
    setTransientFeedbackSignal(null);
    setTransientNewInRotationSignal(null);
    if (feedbackSignalTimerRef.current) {
      window.clearTimeout(feedbackSignalTimerRef.current);
      feedbackSignalTimerRef.current = null;
    }

    if (pendingPlayAfterTrackChangeRef.current) {
      pendingPlayAfterTrackChangeRef.current = false;
      schedulePlayback(() => { void playCurrent('automatic'); });
    }
  }, [capsule?.trackId, pauseAudio, playCurrent, schedulePlayback]);

  useEffect(
    () => () => {
      if (feedbackSignalTimerRef.current) {
        window.clearTimeout(feedbackSignalTimerRef.current);
        feedbackSignalTimerRef.current = null;
      }
      clearNewRotationSignalTimer();
      pauseAudio({ reset: true });
      clearListenHeartbeatTimer();
    },
    [clearListenHeartbeatTimer, clearNewRotationSignalTimer, pauseAudio],
  );

  const goToIndex = useCallback(
    (nextIndex, { keepPlaying = false } = {}) => {
      if (!activeQueue.length) return;
      invalidatePlayback();
      hasUserSelectedTrackRef.current = true;
      cooldownNewRotationSignal();
      endListenSession();
      pendingPlayAfterTrackChangeRef.current = keepPlaying;
      setCurrentIndex(clampIndex(nextIndex, activeQueue.length));
    },
    [activeQueue.length, cooldownNewRotationSignal, endListenSession, invalidatePlayback],
  );

  const goPrevious = () => {
    goToIndex(currentIndex - 1, { keepPlaying: isPlaying });
  };

  const goNext = ({ keepPlaying = isPlaying } = {}) => {
    goToIndex(currentIndex + 1, { keepPlaying });
  };

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    hasUserSelectedTrackRef.current = true;
    cooldownNewRotationSignal();
    if (isPlaying) {
      invalidatePlayback();
      endListenSession();
      audio.pause();
      setIsPlaying(false);
      return;
    }
    void playCurrent(explicitReason());
  };

  const handleLoadedMetadata = () => {
    const nextDuration = Number(audioRef.current?.duration);
    setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
    setIsLoading(false);
  };

  const recordQualifiedPlayIfReady = useCallback(
    async ({ nextElapsed, nextDuration }) => {
      if (
        !player?.token ||
        !capsule ||
        capsule.kind !== RADIO_QUEUE_KINDS.TRACK
      ) {
        return;
      }

      const listenedSeconds = Math.floor(Math.max(0, Number(nextElapsed) || 0));
      const listenPercent =
        nextDuration > 0
          ? Math.min(100, Math.round((listenedSeconds / nextDuration) * 100))
          : 0;

      if (
        listenedSeconds < MIN_PLAY_SECONDS &&
        listenPercent < MIN_PLAY_PERCENT
      ) {
        return;
      }

      const localDayKey = getLocalUtcDayKey();
      const postKey = `${capsule.trackId}:${localDayKey}`;
      if (playPostKeysRef.current.has(postKey)) return;
      playPostKeysRef.current.add(postKey);

      const owner = metricsOwnerRef.current;
      const acceptedMetrics = acceptedMetricsRef.current[capsule.trackId];

      try {
        const response = await axiosConn.post(
          'hitlab/radio/play',
          {
            trackId: capsule.trackId,
            listenSession: {
              listenedSeconds,
              listenPercent,
            },
            clientPlaySessionId: playSessionIdRef.current,
          },
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        const play = response?.data?.data?.play;
        if (owner && metricsOwnerRef.current === owner && play?.trackId === capsule.trackId) {
          if (acceptedMetrics === acceptedMetricsRef.current[play.trackId]) applyLegacyPlayCount(play);
          void refreshRadioMetrics();
        }
      } catch (err) {
        // Play-count telemetry must never interrupt Radio playback.
      }
    },
    [applyLegacyPlayCount, capsule, player?.token, refreshRadioMetrics],
  );

  const handleTimeUpdate = () => {
    const nextElapsed = Number(audioRef.current?.currentTime);
    const safeElapsed = Number.isFinite(nextElapsed) ? nextElapsed : 0;
    const nextDuration = Number(audioRef.current?.duration);
    const safeDuration = Number.isFinite(nextDuration)
      ? nextDuration
      : duration;
    setElapsed(safeElapsed);
    if (isPlaying) {
      void recordQualifiedPlayIfReady({
        nextElapsed: safeElapsed,
        nextDuration: safeDuration,
      });
    }
  };

  const handleEnded = () => {
    const endedElapsed = Number(audioRef.current?.currentTime);
    const endedDuration = Number(audioRef.current?.duration);
    void recordQualifiedPlayIfReady({
      nextElapsed: Number.isFinite(endedElapsed) ? endedElapsed : elapsed,
      nextDuration: Number.isFinite(endedDuration) ? endedDuration : duration,
    });
    endListenSession();
    setIsPlaying(false);
    if (activeQueue.length > 1) {
      goNext({ keepPlaying: true });
    }
  };

  const handleError = () => {
    invalidatePlayback();
    endListenSession();
    setIsPlaying(false);
    setIsLoading(false);
    setErrorMessage('This audio item could not load. Use next to keep listening.');
  };

  const setMinimized = (nextValue) => {
    if (nextValue !== isMinimized) {
      cooldownNewRotationSignal();
    }
    setIsMinimized(nextValue);
    persistMinimizedState(nextValue);
  };

  const expandDock = () => {
    if (conversationFocused) {
      conversationRef.current?.returnToRadio();
      return;
    }
    setMinimized(false);
  };

  const invitationState = useRadioParticipationOwner({ enabled: shouldRender, playerToken: player?.token,
    location, history, isPlaying, trackId: capsule?.trackId, tracks: activeQueue,
    loaded: catalogState !== 'loading', open: expandDock,
    stage: trackId => applyRadioCue({ trackId }), participationOpen: feedbackOpen || Boolean(supportMode), isDock: true });

  const openFeedback = () => {
    if (!capsule?.canGiveFeedback) return;
    cooldownNewRotationSignal();
    setFeedbackOpen(true);
  };

  const closeFeedback = useCallback(() => {
    setFeedbackOpen(false);
  }, []);

  const closeSupport = useCallback(() => {
    setSupportMode(null);
  }, []);

  const goToLogin = () => {
    history.push(`/login?from=${encodeURIComponent(currentRoute)}`);
  };

  const openTipSupport = () => {
    if (!capsule?.hasJgkSupportFields) return;
    cooldownNewRotationSignal();
    setSupportMode('tip');
  };

  const openBuySupport = () => {
    if (!capsule?.hasJgkSupportFields) return;
    cooldownNewRotationSignal();
    setSupportMode('buy');
  };

  const handleSupportSuccess = ({ mode, trackId, result }) => {
    if (mode !== 'buy' || !trackId) return;
    setRadioQueue((currentQueue) =>
      (Array.isArray(currentQueue) ? currentQueue : []).map((item) => {
        const itemTrackId = String(item?.trackId || item?.id || '');
        if (itemTrackId !== trackId) return item;
        return {
          ...item,
          canBuyWithJgk: false,
          canBuySupportWithJgk: false,
          alreadyPurchased: true,
          downloadAvailable: Boolean(result?.downloadAvailable),
          buyUnavailableReason: 'Purchased.',
        };
      }),
    );
  };

  const handleFeedbackSubmitted = (feedback) => {
    if (!capsule?.trackId) return;
    cooldownNewRotationSignal();
    const signal = {
      trackId: capsule.trackId,
      title: capsule.title,
      artistName: capsule.artistName,
      receivedAt: new Date().toISOString(),
      feedbackId: feedback?.feedbackId || feedback?._id || '',
    };
    setTransientFeedbackSignal(signal);
    if (feedbackSignalTimerRef.current) {
      window.clearTimeout(feedbackSignalTimerRef.current);
    }
    feedbackSignalTimerRef.current = window.setTimeout(() => {
      setTransientFeedbackSignal(null);
      feedbackSignalTimerRef.current = null;
    }, 4000);
  };

  const shareRecord = async () => {
    if (!capsule?.canShare) return;
    setShareMessage('');
    const url = getShareUrl('radio', capsule.trackId);
    if (!url) return;
    const shareData = {
      title: 'JOE GALAXY RADIO ROTATION',
      text: `${capsule.title} by ${capsule.artistName}`,
      url,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareMessage('Radio link ready to share.');
        return;
      }
    } catch (err) {
      // Fall through to clipboard fallback.
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        setShareMessage('Radio link copied.');
        return;
      }
    } catch (err) {
      // Fall through to readable status.
    }

    setShareMessage('Radio link is ready.');
  };

  if (!shouldRender) return null;

  const showExpandedPanel = Boolean(!isMinimized && !conversationFocused);

  return (
    <>
      <aside
        ref={radioOwnerRef}
        className={[
          'jg-radio-dock',
          `jg-radio-dock--${placement}`,
          showExpandedPanel ? '' : 'is-minimized',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label="JOE GALAXY RADIO ROTATION"
      >
        {capsule ? (
          <audio
            ref={audioRef}
            crossOrigin={getRadioAudioCrossOrigin(capsule.audioUrl)}
            src={capsule.audioUrl}
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            onPause={handleAudioPause}
            onError={handleError}
            aria-label="JG Radio Dock audio"
          />
        ) : null}

        {!showExpandedPanel ? (
          <RadioLiveCapsule
            capsule={capsule}
            activeQueue={activeQueue}
            currentIndex={currentIndex}
            isPlaying={isPlaying}
            signals={liveCapsuleSignals}
            liveSignals={radioLiveSignals}
            transientFeedbackSignal={transientFeedbackSignal}
            transientNewInRotationSignal={transientNewInRotationSignal}
            transientProofSummary={transientProofSummary}
            audioLevels={radioAudioLevels.levels}
            audioIntensity={radioAudioLevels.intensity}
            isAudioReactive={radioAudioLevels.isBeatReactive}
            onExpand={() => { expandDock(); void captureRadioInteraction({ entrySurface: 'capsule', playerToken: player?.token }); }}
            promotionalCue={cueGlow}
            returnToRadio={conversationFocused}
          />
        ) : (
          <div className="jg-radio-dock-panel">
            <div className="jg-radio-dock-top">
              <div className="jg-radio-dock-brand-lockup">
                <span className="jg-radio-dock-brand-mark-shell" aria-hidden="true">
                  <img
                    className="jg-radio-dock-brand-mark"
                    src={JG_LOGO_MARK_URL}
                    alt=""
                    width="44"
                    height="44"
                    decoding="async"
                  />
                </span>
                <strong>JOE GALAXY RADIO ROTATION</strong>
              </div>
              <button
                type="button"
                className="jg-radio-dock-minimize"
                onClick={() => setMinimized(true)}
                aria-label="Minimize Radio Rotation dock"
              >
                <ChevronDown aria-hidden="true" size={18} />
              </button>
            </div>

            <RadioRecordCapsuleCard
              capsule={capsule}
              activeQueue={activeQueue}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              isLoading={isLoading}
              elapsed={elapsed}
              duration={duration}
              signals={liveCapsuleSignals}
              liveSignals={radioLiveSignals}
              transientFeedbackSignal={transientFeedbackSignal}
              transientNewInRotationSignal={transientNewInRotationSignal}
              transientProofSummary={transientProofSummary}
              audioLevels={radioAudioLevels.levels}
              audioIntensity={radioAudioLevels.intensity}
              isAudioReactive={radioAudioLevels.isBeatReactive}
            />

            <RadioInvitationStatus state={invitationState} />
            {!capsule && <div className="jg-radio-dock-message" role="status">
              <p>{catalogState === 'loading' ? 'Loading Radio…' : catalogState === 'error' ?
                'Radio could not load. Your place is saved.' : 'No records are available right now.'}</p>
              {catalogState !== 'loading' && <button type="button" onClick={() => void loadRadioData()}>Try again</button>}
            </div>}

            {errorMessage ? (
              <p className="jg-radio-dock-message is-error" role="alert">
                {errorMessage}
              </p>
            ) : null}
            {shareMessage ? (
              <p className="jg-radio-dock-message" role="status">
                {shareMessage}
              </p>
            ) : null}

            <RadioRecordActions
              capsule={capsule}
              isPlaying={isPlaying}
              isLoading={isLoading}
              onTogglePlay={togglePlayback}
              onPrevious={goPrevious}
              onNext={() => goNext()}
              onFeedback={openFeedback}
              onShare={shareRecord}
              onTip={openTipSupport}
              onBuySupport={openBuySupport}
            />
          </div>
        )}
        {conversationConfig.enabled && <Suspense fallback={null}>
          <RadioConversation key={currentRoute} ref={conversationRef} surface="dock" ownerRef={radioOwnerRef}
            conversationCount={radioLiveSignals.conversationCount}
            minimized={isMinimized} onFocusedChange={setConversationFocused}
            onPauseRadio={pauseForConversation} onToggleRadio={togglePlayback}
            onPreviousRadio={goPrevious} onNextRadio={() => goNext()} canSkipRadio={activeQueue.length > 1}
            isPlaying={isPlaying} isLoading={isLoading} record={capsule} participationOpen={feedbackOpen || Boolean(supportMode)} />
        </Suspense>}
      </aside>
      {capsule ? (
        <HitLabRadioFeedbackSheet
          open={feedbackOpen}
          track={feedbackTrack}
          player={player}
          elapsed={elapsed}
          duration={duration || capsule.durationSeconds}
          source="joe_galaxy_radio_dock"
          route={currentRoute}
          loginHref={`/login?from=${encodeURIComponent(currentRoute)}`}
          variant="dock"
          onClose={closeFeedback}
          onLogin={goToLogin}
          onSubmitted={handleFeedbackSubmitted}
        />
      ) : null}
      {capsule ? (
        <HitLabRadioJgkSupportSheet
          open={Boolean(supportMode)}
          mode={supportMode || 'tip'}
          track={capsule}
          player={player}
          route={currentRoute}
          loginHref={`/login?from=${encodeURIComponent(currentRoute)}`}
          variant="dock"
          onClose={closeSupport}
          onLogin={goToLogin}
          onSuccess={handleSupportSuccess}
        />
      ) : null}
    </>
  );
}

export { MINIMIZED_STORAGE_KEY };
